import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST;
const POSTHOG_ENABLED = process.env.REACT_APP_POSTHOG_ENABLED === 'true';
const APP_VERSION = process.env.REACT_APP_VERSION;


// Get from localstorage userData.profile if abssent is public route
export const isPublicRoute = () => {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return true; // no userData → treat as public
    const userData = JSON.parse(raw);
    return !userData?.profile;
  } catch {
    return true; // malformed → fail closed to "public"
  }
};

const PII_REGEXES = [
  // Italian fiscal code (codice fiscale)
  /\b[A-Z]{6}\d{2}[A-EHLMPRST]\d{2}[A-Z]\d{3}[A-Z]\b/gi,
  // Italian VAT (partita IVA)
  /\b\d{11}\b/g,
  // IBAN (broad)
  /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/gi,
  // POD (Italian electricity supply point: IT + 3 digits + E + 7 alphanumerics)
  /\bIT\d{3}E[A-Z0-9]{7,8}\b/gi,
  // Email
  /[\w.+-]+@[\w-]+\.[\w.-]+/g,
  // Italian phone (mobile/landline 9–11 digits, optional +39)
  /(?:\+?39[\s-]?)?\b3\d{2}[\s-]?\d{6,8}\b/g,
];

const PROPERTY_DENYLIST_KEYS = new Set([
  'last_name', 'lastname', 'cognome',
  'email', 'email2', 'e_mail',
  'phone', 'mobile_phone', 'telefono', 'cellulare',
  'pod', 'customer_pod', 'maintenance_pod',
  'customer_last_name', 'maintenance_customer_last_name',
  'customer_phone', 'customer_mobile_phone',
  'fiscal_number', 'codice_fiscale', 'cf',
  'birth_date', 'data_di_nascita',
  'birth_place', 'luogo_di_nascita',
  'address', 'indirizzo',
  'hermes_ref', 'rif_hermes',
]);

// Fingerprintable / device-identifying props stripped on every event.
// Boss wants no identify + no persistence → also kill the silent fingerprint
// vectors PostHog ships by default.
const FINGERPRINT_PROPS_TO_STRIP = [
  '$ip',
  '$geoip_city_name',
  '$geoip_postal_code',
  '$geoip_latitude',
  '$geoip_longitude',
  '$geoip_subdivision_1_name',
  '$geoip_subdivision_1_code',
  '$geoip_subdivision_2_name',
  '$geoip_country_code',
  '$geoip_country_name',
  '$geoip_continent_code',
  '$geoip_continent_name',
  '$geoip_time_zone',
  '$initial_referrer',
  '$initial_referring_domain',
  '$referrer',
  '$referring_domain',
  '$screen_height',
  '$screen_width',
  '$viewport_height',
  '$viewport_width',
  '$raw_user_agent',
  '$user_agent',
  '$device_id',
  '$anon_distinct_id',
  '$session_id',
  '$window_id',
];

const scrubString = (value) => {
  if (typeof value !== 'string' || value.length === 0) return value;
  let scrubbed = value;
  for (const re of PII_REGEXES) {
    scrubbed = scrubbed.replace(re, '[REDACTED]');
  }
  return scrubbed;
};

// Replace numeric / UUID path segments with `:id` so /customers/12345 -> /customers/:id
const ID_SEGMENT = /\/(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\/|$)/gi;

const scrubUrl = (raw) => {
  if (typeof raw !== 'string' || raw.length === 0) return raw;
  try {
    const url = new URL(raw, 'http://_local_');
    url.search = '';
    url.hash = '';
    const path = url.pathname.replace(ID_SEGMENT, '/:id');
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      return `${url.origin}${path}`;
    }
    return path;
  } catch {
    return raw.split('?')[0].split('#')[0].replace(ID_SEGMENT, '/:id');
  }
};

const URL_PROPERTY_KEYS = new Set([
  '$current_url',
  '$pathname',
  '$initial_current_url',
  '$initial_pathname',
  '$host',
  '$session_entry_url',
  '$session_entry_pathname',
  '$session_entry_host',
  '$session_entry_referrer',
  '$session_entry_referring_domain',
  '$prev_pageview_url',
  '$prev_pageview_pathname',
  '$prev_pageview_host',
]);

const scrubProperties = (props) => {
  if (!props || typeof props !== 'object') return props;
  const out = Array.isArray(props) ? [] : {};
  for (const [key, value] of Object.entries(props)) {
    if (PROPERTY_DENYLIST_KEYS.has(String(key).toLowerCase())) {
      out[key] = '[REDACTED]';
      continue;
    }
    if (URL_PROPERTY_KEYS.has(key) && typeof value === 'string') {
      out[key] = scrubString(scrubUrl(value));
      continue;
    }
    if (value && typeof value === 'object') {
      out[key] = scrubProperties(value);
    } else if (typeof value === 'string') {
      out[key] = scrubString(value);
    } else {
      out[key] = value;
    }
  }
  return out;
};

export const initPostHog = () => {
  // Idempotent: skip if already initialized
  if (posthog.__loaded) return posthog;

  if (!POSTHOG_ENABLED || !POSTHOG_KEY) {
    console.log('[PostHog] Disabled or missing key, skipping initialization');
    return;
  }

  if (isPublicRoute()) {
    console.log('[PostHog] Public route detected, skipping initialization');
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,

    // Pageview tracking - automatic SPA mode (heatmaps need this)
    capture_pageview: true,
    capture_pageleave: true,

    // Session replay disabled
    disable_session_recording: true,
    enable_heatmaps: true,
    // Autocapture enabled
    autocapture: {
      dom_event_allowlist: ['click', 'submit'],
      element_allowlist: ['a', 'button', 'form', 'input', 'select', 'textarea']
    },

    // Built-in PII scrub for common patterns (credit card, etc.)
    mask_personal_data_properties: true,

    mask_all_element_attributes: true,
    mask_all_text: true,

    // No identification, no person profiles. Boss directive.
    person_profiles: 'identified_only', // and we never call identify()

    // No persistence - memory only, dies with the tab. Boss directive.
    persistence: 'memory',
    disable_persistence: true,

    // Drop client IP at SDK level; pair with "Discard client IP data" toggle
    // in PostHog project settings for full coverage.
    ip: false,

    // Drop fingerprint props at registration time so they're not captured
    // server-side either.
    property_denylist: FINGERPRINT_PROPS_TO_STRIP,

    // Last-line-of-defense scrubber - runs on every event before send.
    // Defensively strips fingerprint props, scrubs PII keys/values, sanitizes
    // URLs, and drops any $identify/$set events that slip through.
    before_send: (event) => {
      if (!event) return null;
      // Hard guarantee: never ship identify/set events even if some library calls them.
      if (event.event === '$identify' || event.event === '$set' || event.event === '$create_alias') {
        return null;
      }
      try {
        if (event.properties) {
          for (const prop of FINGERPRINT_PROPS_TO_STRIP) {
            delete event.properties[prop];
          }
          event.properties = scrubProperties(event.properties);
        }
        if (event.$set) event.$set = scrubProperties(event.$set);
        if (event.$set_once) event.$set_once = scrubProperties(event.$set_once);
      } catch (e) {
        console.warn('[PH] scrub failed:', e);
      }
      return event;
    },

    // Attach safe app-level globals to every event
    loaded: (ph) => {
      const profile = JSON.parse(localStorage.getItem('userData')).profile;
      ph.register({
        app: 'hermes',
        app_version: APP_VERSION,
        user_role: profile
      });
      if (process.env.REACT_APP_STAGE === 'development') {
        ph.debug();
      }
    },
    respect_dnt: false,
  });
  return posthog;
};

export { posthog };
export default posthog;
